import { useState, useRef, useCallback, useEffect } from 'react'
export interface DragItem {
  id: string
  index: number
  droppableId: string
}

export interface DragState {
  isDragging: boolean
  draggedItem: DragItem | null
  dragOffset: { x: number; y: number } | null
  currentDropZone: string | null
  currentDropIndex: number | null
}

export interface UseDragDropReturn {
  dragState: DragState
  startDrag: (e: React.MouseEvent | React.TouchEvent, item: DragItem, element: HTMLElement) => void
  endDrag: (
    e: MouseEvent | TouchEvent,
    onDrop: (source: DragItem, destination: { droppableId: string; index: number } | null) => void
  ) => void
  handleMouseMove: (e: MouseEvent | TouchEvent) => void
}

/**
 * Custom drag and drop hook for simple vertical list reordering
 */
export function useCustomDragDrop(): UseDragDropReturn {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItem: null,
    dragOffset: null,
    currentDropZone: null,
    currentDropIndex: null,
  })

  const draggedElementRef = useRef<HTMLElement | null>(null)
  const initialMousePosRef = useRef<{ x: number; y: number } | null>(null)
  const currentMousePosRef = useRef<{ x: number; y: number } | null>(null)
  const isEndingDragRef = useRef(false)
  const originalElementStylesRef = useRef<{
    position: string
    left: string
    top: string
    zIndex: string
    pointerEvents: string
  } | null>(null)
  const dropInfoRef = useRef<{
    source: DragItem
    destination: { droppableId: string; index: number } | null
  } | null>(null)
  const hasDraggedRef = useRef(false) // Track if user actually moved the mouse
  const lastDropIndexRef = useRef<{ droppableId: string | null; index: number | null }>({
    droppableId: null,
    index: null,
  })
  const DRAG_THRESHOLD = 5 // Minimum pixels to move before drag starts

  const startDrag = useCallback((e: React.MouseEvent | React.TouchEvent, item: DragItem, element: HTMLElement) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

    const rect = element.getBoundingClientRect()
    const offsetX = clientX - rect.left
    const offsetY = clientY - rect.top

    initialMousePosRef.current = { x: clientX, y: clientY }
    draggedElementRef.current = element
    isEndingDragRef.current = false // Reset flag when starting new drag
    hasDraggedRef.current = false // Reset drag flag

    // Save original styles to restore later
    originalElementStylesRef.current = {
      position: element.style.position || '',
      left: element.style.left || '',
      top: element.style.top || '',
      zIndex: element.style.zIndex || '',
      pointerEvents: element.style.pointerEvents || '',
    }

    setDragState({
      isDragging: true,
      draggedItem: item,
      dragOffset: { x: offsetX, y: offsetY },
      currentDropZone: null,
      currentDropIndex: null,
    })

    // Prevent default to avoid text selection
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!dragState.isDragging || !initialMousePosRef.current) return

      const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY

      if (!clientX || !clientY) return

      // Check if user has moved enough to start dragging
      if (!hasDraggedRef.current && initialMousePosRef.current) {
        const deltaX = Math.abs(clientX - initialMousePosRef.current.x)
        const deltaY = Math.abs(clientY - initialMousePosRef.current.y)
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

        if (distance < DRAG_THRESHOLD) {
          // Not enough movement yet, don't proceed with drag
          return
        }

        // Mark that dragging has started
        hasDraggedRef.current = true
      }

      // Track current mouse position
      currentMousePosRef.current = { x: clientX, y: clientY }

      // Update drag preview position
      if (draggedElementRef.current) {
        const offset = dragState.dragOffset!
        draggedElementRef.current.style.position = 'fixed'
        draggedElementRef.current.style.left = `${clientX - offset.x}px`
        draggedElementRef.current.style.top = `${clientY - offset.y}px`
        draggedElementRef.current.style.zIndex = '1000'
        draggedElementRef.current.style.pointerEvents = 'none'
      }

      // Find drop zone under cursor
      const elementBelow = document.elementFromPoint(clientX, clientY)
      const dropZone = elementBelow?.closest('[data-droppable-id]')
      const dropZoneId = dropZone?.getAttribute('data-droppable-id') || null

      // Calculate insertion index
      let dropIndex: number | null = null
      if (dropZoneId && dropZone && dragState.draggedItem) {
        const items = Array.from(dropZone.querySelectorAll('[data-draggable-id]')) as HTMLElement[]
        const sourceId = dragState.draggedItem.id
        const sourceIndex = dragState.draggedItem.index
        const isSameDroppable = dropZoneId === dragState.draggedItem.droppableId

        if (isSameDroppable) {
          // Same droppable - filter out the dragged item
          const filteredItems = items.filter(item => {
            const itemId = item.getAttribute('data-draggable-id')
            return itemId !== sourceId
          })

          // Find insertion position using filtered items
          dropIndex = filteredItems.length // Default to end

          for (let i = 0; i < filteredItems.length; i++) {
            const item = filteredItems[i]
            const rect = item.getBoundingClientRect()
            const itemCenterY = rect.top + rect.height / 2

            if (clientY < itemCenterY) {
              // Find the original index of this item
              const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)

              // Convert to index in filtered list (same logic as endDrag)
              if (originalIndex > sourceIndex) {
                dropIndex = originalIndex - 1
              } else {
                dropIndex = originalIndex
              }
              break
            }
          }
        } else {
          // Different droppable - use ALL items (same logic as endDrag)
          dropIndex = items.length // Default to end

          if (items.length === 0) {
            // Empty list - drop at beginning
            dropIndex = 0
          } else {
            // Check if we're hovering above the first item
            const firstItem = items[0]
            const firstItemRect = firstItem.getBoundingClientRect()
            const firstItemTop = firstItemRect.top
            const firstItemCenterY = firstItemRect.top + firstItemRect.height / 2

            // If cursor is above first item's top OR in the top half of first item, drop at beginning
            if (clientY < firstItemTop || clientY < firstItemCenterY) {
              dropIndex = 0
            } else {
              // Check all items - we need to detect hovering above the last item correctly
              let found = false
              for (let i = 0; i < items.length; i++) {
                const item = items[i]
                const rect = item.getBoundingClientRect()
                const itemCenterY = rect.top + rect.height / 2
                const itemBottom = rect.bottom

                if (clientY < itemCenterY) {
                  // We're hovering above the center of this item - insert before it
                  const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)
                  dropIndex = originalIndex
                  found = true
                  break
                } else if (i === items.length - 1) {
                  // This is the last item
                  const itemHeight = rect.height
                  const thresholdFromCenter = itemCenterY + itemHeight * 0.25

                  if (clientY >= itemBottom) {
                    // Clearly below the bottom edge - insert after
                    dropIndex = items.length
                  } else if (clientY >= thresholdFromCenter) {
                    // In the lower quarter of the item - insert after
                    dropIndex = items.length
                  } else {
                    // Between center and threshold - insert before (in the upper half)
                    const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)
                    dropIndex = originalIndex
                  }
                  found = true
                  break
                }
              }

              // If we didn't find a position and we're below all items, drop at end
              if (!found) {
                dropIndex = items.length
              }
            }
          }
        }
      }

      // Only update if drop index or drop zone changed to prevent unnecessary re-renders
      const hasChanged =
        lastDropIndexRef.current.droppableId !== dropZoneId || lastDropIndexRef.current.index !== dropIndex

      if (hasChanged) {
        lastDropIndexRef.current = {
          droppableId: dropZoneId,
          index: dropIndex,
        }

        setDragState(prev => ({
          ...prev,
          currentDropZone: dropZoneId,
          currentDropIndex: dropIndex,
        }))
      }
    },
    [dragState.isDragging, dragState.dragOffset, dragState.draggedItem]
  )

  const cancelDrag = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedItem: null,
      dragOffset: null,
      currentDropZone: null,
      currentDropIndex: null,
    })
    // Restore original styles
    if (draggedElementRef.current && originalElementStylesRef.current) {
      draggedElementRef.current.style.position = originalElementStylesRef.current.position
      draggedElementRef.current.style.left = originalElementStylesRef.current.left
      draggedElementRef.current.style.top = originalElementStylesRef.current.top
      draggedElementRef.current.style.zIndex = originalElementStylesRef.current.zIndex
      draggedElementRef.current.style.pointerEvents = originalElementStylesRef.current.pointerEvents
    }
    // Reset refs
    initialMousePosRef.current = null
    currentMousePosRef.current = null
    draggedElementRef.current = null
    originalElementStylesRef.current = null
    isEndingDragRef.current = false
    hasDraggedRef.current = false
    dropInfoRef.current = null
    lastDropIndexRef.current = {
      droppableId: null,
      index: null,
    }
  }, [])

  const endDrag = useCallback(
    (
      e: MouseEvent | TouchEvent,
      onDrop: (source: DragItem, destination: { droppableId: string; index: number } | null) => void
    ) => {
      // Prevent double-firing
      if (isEndingDragRef.current) {
        return
      }

      // If user didn't actually drag (just clicked), cancel the drag
      if (!hasDraggedRef.current) {
        setDragState({
          isDragging: false,
          draggedItem: null,
          dragOffset: null,
          currentDropZone: null,
          currentDropIndex: null,
        })
        // Restore original styles
        if (draggedElementRef.current && originalElementStylesRef.current) {
          draggedElementRef.current.style.position = originalElementStylesRef.current.position
          draggedElementRef.current.style.left = originalElementStylesRef.current.left
          draggedElementRef.current.style.top = originalElementStylesRef.current.top
          draggedElementRef.current.style.zIndex = originalElementStylesRef.current.zIndex
          draggedElementRef.current.style.pointerEvents = originalElementStylesRef.current.pointerEvents
        }
        // Reset refs
        initialMousePosRef.current = null
        currentMousePosRef.current = null
        draggedElementRef.current = null
        originalElementStylesRef.current = null
        isEndingDragRef.current = false
        hasDraggedRef.current = false
        dropInfoRef.current = null
        lastDropIndexRef.current = {
          droppableId: null,
          index: null,
        }
        return
      }

      isEndingDragRef.current = true

      // Get mouse position from the event first
      const clientX = 'touches' in e ? e.changedTouches[0]?.clientX || e.touches[0]?.clientX : e.clientX
      const clientY = 'touches' in e ? e.changedTouches[0]?.clientY || e.touches[0]?.clientY : e.clientY

      // Find final drop zone
      const elementBelow = document.elementFromPoint(clientX, clientY)
      const dropZone = elementBelow?.closest('[data-droppable-id]')
      const dropZoneId = dropZone?.getAttribute('data-droppable-id')

      // Calculate destination before updating state
      let calculatedDestination: { droppableId: string; index: number } | null = null

      if (dropZoneId && dropZone) {
        // Find drop index within the drop zone
        const items = Array.from(dropZone.querySelectorAll('[data-draggable-id]')) as HTMLElement[]

        // Calculate drop position based on which item we're hovering over
        let dropIndex = items.length // Default to end

        if (items.length === 0) {
          // Empty list - drop at beginning
          dropIndex = 0
        } else {
          // Check if we're hovering above the first item
          const firstItem = items[0]
          const firstItemRect = firstItem.getBoundingClientRect()
          const firstItemTop = firstItemRect.top
          const firstItemCenterY = firstItemRect.top + firstItemRect.height / 2

          // If cursor is above first item's top OR in the top half of first item, drop at beginning
          if (clientY < firstItemTop || clientY < firstItemCenterY) {
            dropIndex = 0
          } else {
            // Check all items - we need to detect hovering above the last item correctly
            let found = false
            for (let i = 0; i < items.length; i++) {
              const item = items[i]
              const rect = item.getBoundingClientRect()
              const itemCenterY = rect.top + rect.height / 2
              const itemBottom = rect.bottom

              if (clientY < itemCenterY) {
                // We're hovering above the center of this item - insert before it
                const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)
                dropIndex = originalIndex
                found = true
                break
              } else if (i === items.length - 1) {
                // This is the last item
                // If we're below the bottom edge of the last item, insert after it
                // If we're above the bottom but below the center, check if we're closer to bottom or center
                // Use a threshold: if we're more than 25% of item height below center, drop after
                const itemHeight = rect.height
                const thresholdFromCenter = itemCenterY + itemHeight * 0.25

                if (clientY >= itemBottom) {
                  // Clearly below the bottom edge - insert after
                  dropIndex = items.length
                } else if (clientY >= thresholdFromCenter) {
                  // In the lower quarter of the item - insert after
                  dropIndex = items.length
                } else {
                  // Between center and threshold - insert before (in the upper half)
                  const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)
                  dropIndex = originalIndex
                }
                found = true
                break
              }
            }

            // If we didn't find a position and we're below all items, drop at end
            if (!found) {
              dropIndex = items.length
            }
          }
        }

        calculatedDestination = { droppableId: dropZoneId, index: dropIndex }
      }

      // Get current state and calculate final destination with sourceId
      setDragState(currentState => {
        if (!currentState.isDragging || !currentState.draggedItem) {
          isEndingDragRef.current = false
          return currentState
        }

        // Adjust destination if dragging within same droppable
        let finalDestination = calculatedDestination
        if (calculatedDestination && dropZoneId && dropZone) {
          const items = Array.from(dropZone.querySelectorAll('[data-draggable-id]')) as HTMLElement[]
          const sourceId = currentState.draggedItem.id
          const sourceIndex = currentState.draggedItem.index

          // If same droppable, filter out the dragged item and recalculate drop index
          if (calculatedDestination.droppableId === currentState.draggedItem.droppableId) {
            // Filter out the dragged item to get the correct drop position
            const filteredItems = items.filter(item => {
              const itemId = item.getAttribute('data-draggable-id')
              return itemId !== sourceId
            })

            // Recalculate drop index based on filtered items (without the dragged item)
            // The move() function expects the destination index in the list AFTER removing the source
            // The visual placeholder shows insert BEFORE an item, so we need to match that behavior
            let dropIndex = filteredItems.length // Default to end

            for (let i = 0; i < filteredItems.length; i++) {
              const item = filteredItems[i]
              const rect = item.getBoundingClientRect()
              const itemCenterY = rect.top + rect.height / 2
              const originalIndex = parseInt(item.getAttribute('data-draggable-index') || '0', 10)

              if (clientY < itemCenterY) {
                // We're hovering above this item - we want to insert BEFORE it
                // The dropIndex should be the position in the filtered list where we insert
                //
                // Example: [A(0), B(1), C(2)] dragging A to before C
                // - Filtered: [B(0), C(1)]
                // - Hover above C: i=1 (filtered index)
                // - dropIndex = 1
                // - move(0, 1, [A,B,C]) → [B, A, C] ✓
                //
                // Example: [A(0), B(1), C(2)] dragging A to after B (position 1)
                // - Filtered: [B(0), C(1)]
                // - User hovers "below B" which might be detected as hovering above C
                // - Hover above C: i=1
                // - dropIndex = 1
                // - move(0, 1, [A,B,C]) → [B, A, C] ✓ (A at position 1, after B)
                //
                // If user hovers above B instead:
                // - Hover above B: i=0
                // - dropIndex = 0
                // - move(0, 0, [A,B,C]) → no change ✗
                // - But we want A after B, so dropIndex should be 1
                //
                // Solution: When dragging from index 0 and hovering above the first filtered item,
                // interpret it as "insert after" instead of "insert before"
                if (sourceIndex === 0 && i === 0 && originalIndex === sourceIndex + 1) {
                  // Dragging from beginning, hovering above the first filtered item (which is the second item)
                  // Interpret as "insert after" this item
                  dropIndex = i + 1
                } else {
                  // Normal case: insert before the item
                  dropIndex = i
                }

                break
              }
            }

            finalDestination = { ...calculatedDestination, index: dropIndex }
          } else {
            // Different droppable - use calculatedDestination directly
            // Ensure index 0 is preserved correctly
            finalDestination = calculatedDestination
          }
        } else if (!calculatedDestination) {
          // No destination found - this shouldn't happen but handle it
          finalDestination = null
        }

        // Store drop info before resetting state
        dropInfoRef.current = {
          source: currentState.draggedItem,
          destination: finalDestination,
        }

        // Return reset state
        return {
          isDragging: false,
          draggedItem: null,
          dragOffset: null,
          currentDropZone: null,
          currentDropIndex: null,
        }
      })

      // Reset last drop index ref
      lastDropIndexRef.current = {
        droppableId: null,
        index: null,
      }

      // Reset element styles and call onDrop after state update
      setTimeout(() => {
        if (draggedElementRef.current && originalElementStylesRef.current) {
          draggedElementRef.current.style.position = originalElementStylesRef.current.position
          draggedElementRef.current.style.left = originalElementStylesRef.current.left
          draggedElementRef.current.style.top = originalElementStylesRef.current.top
          draggedElementRef.current.style.zIndex = originalElementStylesRef.current.zIndex
          draggedElementRef.current.style.pointerEvents = originalElementStylesRef.current.pointerEvents
        }

        // Call onDrop after visual reset
        if (dropInfoRef.current) {
          onDrop(dropInfoRef.current.source, dropInfoRef.current.destination)
        }

        // Reset refs
        initialMousePosRef.current = null
        currentMousePosRef.current = null
        draggedElementRef.current = null
        originalElementStylesRef.current = null
        isEndingDragRef.current = false
        hasDraggedRef.current = false
        dropInfoRef.current = null
      }, 0)
    },
    []
  )

  // Handle global mouse/touch events and keyboard (Escape to cancel)
  useEffect(() => {
    if (!dragState.isDragging) {
      // Reset cursor when not dragging
      document.body.style.cursor = ''
      return
    }

    // Set grabbing cursor on body when dragging
    document.body.style.cursor = 'grabbing'

    const handleMove = (e: MouseEvent | TouchEvent) => handleMouseMove(e)
    const handleEnd = () => {
      // This will be called by explicit endDrag call
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        cancelDrag()
      }
    }

    window.addEventListener('mousemove', handleMove, { passive: false })
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('mouseup', handleEnd)
    window.addEventListener('touchend', handleEnd)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.cursor = ''
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchend', handleEnd)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [dragState.isDragging, handleMouseMove, cancelDrag])

  return {
    dragState,
    startDrag,
    endDrag,
    handleMouseMove,
  }
}
